import { useState, useEffect } from 'react';
import { Icon, Button, Input } from '@/components/atoms';
import { Modal } from '@/components/molecules';
import type { Discount, CreateDiscount } from '@/schemas/discounts';
import { createDiscountSchema, DiscountType } from '@/schemas/discounts';
import { useProductsList, useWarehousesList } from '@/hooks/useErpApi';

interface DiscountFormProps {
  isOpen: boolean;
  onClose: () => void;
  existingDiscount?: Discount;
  onSuccess: () => void;
  onSubmit: (data: CreateDiscount) => Promise<void>;
}

export const DiscountForm: React.FC<DiscountFormProps> = ({
  isOpen,
  onClose,
  existingDiscount,
  onSuccess,
  onSubmit,
}) => {
  const { data: products } = useProductsList();
  const { data: warehouses } = useWarehousesList();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const getInitialFormData = (): CreateDiscount => {
    if (existingDiscount) {
      return {
        code: existingDiscount.code,
        name: existingDiscount.name,
        description: existingDiscount.description,
        discount_type: existingDiscount.discount_type,
        value: existingDiscount.value,
        min_order_value: existingDiscount.min_order_value,
        max_order_value: existingDiscount.max_order_value,
        max_discount_amount: existingDiscount.max_discount_amount,
        valid_from: existingDiscount.valid_from.split('T')[0],
        valid_until: existingDiscount.valid_until.split('T')[0],
        usage_limit: existingDiscount.usage_limit,
        is_active: existingDiscount.is_active,
        is_stackable: existingDiscount.is_stackable,
        priority: existingDiscount.priority,
        applicable_products: existingDiscount.applicable_products,
        applicable_categories: existingDiscount.applicable_categories,
        applicable_warehouses: existingDiscount.applicable_warehouses,
        excluded_products: existingDiscount.excluded_products,
        excluded_categories: existingDiscount.excluded_categories,
        buy_quantity: existingDiscount.buy_quantity,
        get_quantity: existingDiscount.get_quantity,
        get_discount_type: existingDiscount.get_discount_type,
        get_discount_value: existingDiscount.get_discount_value,
        terms: existingDiscount.terms,
      };
    }

    return {
      code: '',
      name: '',
      description: '',
      discount_type: DiscountType.PERCENTAGE,
      value: 0,
      valid_from: new Date().toISOString().split('T')[0],
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      is_active: true,
      is_stackable: false,
      priority: 0,
    };
  };

  const [formData, setFormData] = useState<CreateDiscount>(getInitialFormData());

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormData());
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingDiscount]);

  const handleInputChange = (
    field: keyof CreateDiscount,
    value: string | number | boolean | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    try {
      createDiscountSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof Error && 'errors' in error) {
        const zodError = error as { errors: Array<{ path: string[]; message: string }> };
        const newErrors: Record<string, string> = {};
        zodError.errors.forEach((err) => {
          if (err.path.length > 0) {
            newErrors[err.path[0]] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onSuccess();
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        setErrors({ submit: error.message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const discountTypes = [
    { value: DiscountType.FLAT, label: 'Flat Amount', icon: 'DollarSign' },
    { value: DiscountType.PERCENTAGE, label: 'Percentage', icon: 'Percent' },
    { value: DiscountType.BUY_X_GET_Y, label: 'Buy X Get Y', icon: 'Gift' },
    { value: DiscountType.SEASONAL, label: 'Seasonal', icon: 'Calendar' },
    { value: DiscountType.BULK, label: 'Bulk Purchase', icon: 'Package' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={existingDiscount ? 'Edit Discount' : 'Create Discount'}
      size="xl"
    >
      <div className="space-y-6 max-h-[70vh] overflow-y-auto px-1">
        {/* Error Display */}
        {errors.submit && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Icon name="CircleAlert" size={20} className="text-error-600" />
              <p className="text-error-700">{errors.submit}</p>
            </div>
          </div>
        )}

        {/* Section 1: Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Icon name="Info" size={20} />
            Basic Information
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Discount Code <span className="text-error-600">*</span>
              </label>
              <Input
                placeholder="e.g., SUMMER2024"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                disabled={isSubmitting || !!existingDiscount}
                className={errors.code ? 'border-error-300' : ''}
              />
              {errors.code && <p className="mt-1 text-sm text-error-600">{errors.code}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Discount Name <span className="text-error-600">*</span>
              </label>
              <Input
                placeholder="e.g., Summer Sale"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={isSubmitting}
                className={errors.name ? 'border-error-300' : ''}
              />
              {errors.name && <p className="mt-1 text-sm text-error-600">{errors.name}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Description</label>
            <textarea
              placeholder="Optional description of this discount"
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={isSubmitting}
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active || false}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
              disabled={isSubmitting}
              className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-neutral-700">
              Active (discount is enabled)
            </label>
          </div>
        </div>

        {/* Section 2: Discount Type & Value */}
        <div className="space-y-4 pt-6 border-t border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Icon name="Percent" size={20} />
            Discount Type & Value
          </h3>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-3">
              Discount Type <span className="text-error-600">*</span>
            </label>
            <div className="grid grid-cols-5 gap-3">
              {discountTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleInputChange('discount_type', type.value)}
                  disabled={isSubmitting}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.discount_type === type.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <Icon
                    name={type.icon as 'DollarSign' | 'Percent' | 'Gift' | 'Calendar' | 'Package'}
                    size={24}
                    className={`mx-auto mb-2 ${
                      formData.discount_type === type.value
                        ? 'text-primary-600'
                        : 'text-neutral-400'
                    }`}
                  />
                  <p
                    className={`text-xs font-medium ${
                      formData.discount_type === type.value
                        ? 'text-primary-700'
                        : 'text-neutral-700'
                    }`}
                  >
                    {type.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Conditional Fields Based on Type */}
          {formData.discount_type === DiscountType.FLAT && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Discount Amount (₹) <span className="text-error-600">*</span>
              </label>
              <Input
                type="number"
                placeholder="e.g., 50"
                value={formData.value || ''}
                onChange={(e) => handleInputChange('value', parseFloat(e.target.value) || 0)}
                disabled={isSubmitting}
                min="0"
                step="0.01"
              />
            </div>
          )}

          {formData.discount_type === DiscountType.PERCENTAGE && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Discount Percentage (%) <span className="text-error-600">*</span>
                </label>
                <Input
                  type="number"
                  placeholder="e.g., 10"
                  value={formData.value || ''}
                  onChange={(e) => handleInputChange('value', parseFloat(e.target.value) || 0)}
                  disabled={isSubmitting}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Max Discount Cap (₹)
                </label>
                <Input
                  type="number"
                  placeholder="Optional cap"
                  value={formData.max_discount_amount || ''}
                  onChange={(e) =>
                    handleInputChange(
                      'max_discount_amount',
                      parseFloat(e.target.value) || undefined
                    )
                  }
                  disabled={isSubmitting}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          )}

          {formData.discount_type === DiscountType.BUY_X_GET_Y && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Buy Quantity <span className="text-error-600">*</span>
                </label>
                <Input
                  type="number"
                  placeholder="e.g., 2"
                  value={formData.buy_quantity || ''}
                  onChange={(e) =>
                    handleInputChange('buy_quantity', parseInt(e.target.value) || undefined)
                  }
                  disabled={isSubmitting}
                  min="1"
                  step="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Get Quantity <span className="text-error-600">*</span>
                </label>
                <Input
                  type="number"
                  placeholder="e.g., 1"
                  value={formData.get_quantity || ''}
                  onChange={(e) =>
                    handleInputChange('get_quantity', parseInt(e.target.value) || undefined)
                  }
                  disabled={isSubmitting}
                  min="1"
                  step="1"
                />
              </div>
            </div>
          )}

          {formData.discount_type === DiscountType.BULK && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Minimum Quantity <span className="text-error-600">*</span>
                </label>
                <Input
                  type="number"
                  placeholder="e.g., 10"
                  value={formData.buy_quantity || ''}
                  onChange={(e) =>
                    handleInputChange('buy_quantity', parseInt(e.target.value) || undefined)
                  }
                  disabled={isSubmitting}
                  min="1"
                  step="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Discount Value (%) <span className="text-error-600">*</span>
                </label>
                <Input
                  type="number"
                  placeholder="e.g., 5"
                  value={formData.value || ''}
                  onChange={(e) => handleInputChange('value', parseFloat(e.target.value) || 0)}
                  disabled={isSubmitting}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Applicability Filters */}
        <div className="space-y-4 pt-6 border-t border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Icon name="SlidersHorizontal" size={20} />
            Applicability (Optional - defaults to all)
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Applicable Products
              </label>
              <select
                multiple
                value={formData.applicable_products?.split(',').filter(Boolean) || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                  handleInputChange(
                    'applicable_products',
                    selected.length > 0 ? selected.join(',') : undefined
                  );
                }}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100"
                size={4}
              >
                {products?.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-neutral-600">Hold Ctrl/Cmd to select multiple</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Applicable Warehouses
              </label>
              <select
                multiple
                value={formData.applicable_warehouses?.split(',').filter(Boolean) || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                  handleInputChange(
                    'applicable_warehouses',
                    selected.length > 0 ? selected.join(',') : undefined
                  );
                }}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100"
                size={4}
              >
                {warehouses?.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-neutral-600">Hold Ctrl/Cmd to select multiple</p>
            </div>
          </div>
        </div>

        {/* Section 4: Constraints */}
        <div className="space-y-4 pt-6 border-t border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Icon name="Lock" size={20} />
            Constraints
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Min Order Value (₹)
              </label>
              <Input
                type="number"
                placeholder="Optional minimum"
                value={formData.min_order_value || ''}
                onChange={(e) =>
                  handleInputChange('min_order_value', parseFloat(e.target.value) || undefined)
                }
                disabled={isSubmitting}
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Max Order Value (₹)
              </label>
              <Input
                type="number"
                placeholder="Optional maximum"
                value={formData.max_order_value || ''}
                onChange={(e) =>
                  handleInputChange('max_order_value', parseFloat(e.target.value) || undefined)
                }
                disabled={isSubmitting}
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Total Usage Limit
              </label>
              <Input
                type="number"
                placeholder="Unlimited if empty"
                value={formData.usage_limit || ''}
                onChange={(e) =>
                  handleInputChange('usage_limit', parseInt(e.target.value) || undefined)
                }
                disabled={isSubmitting}
                min="1"
                step="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Priority (higher = applied first)
              </label>
              <Input
                type="number"
                placeholder="0"
                value={formData.priority || 0}
                onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 0)}
                disabled={isSubmitting}
                min="0"
                step="1"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_stackable"
              checked={formData.is_stackable || false}
              onChange={(e) => handleInputChange('is_stackable', e.target.checked)}
              disabled={isSubmitting}
              className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="is_stackable" className="text-sm font-medium text-neutral-700">
              Combinable with other discounts
            </label>
          </div>
        </div>

        {/* Section 5: Validity Period */}
        <div className="space-y-4 pt-6 border-t border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Icon name="Calendar" size={20} />
            Validity Period
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Valid From <span className="text-error-600">*</span>
              </label>
              <Input
                type="date"
                value={formData.valid_from}
                onChange={(e) => handleInputChange('valid_from', e.target.value)}
                disabled={isSubmitting}
                className={errors.valid_from ? 'border-error-300' : ''}
              />
              {errors.valid_from && (
                <p className="mt-1 text-sm text-error-600">{errors.valid_from}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Valid Until <span className="text-error-600">*</span>
              </label>
              <Input
                type="date"
                value={formData.valid_until}
                onChange={(e) => handleInputChange('valid_until', e.target.value)}
                disabled={isSubmitting}
                className={errors.valid_until ? 'border-error-300' : ''}
              />
              {errors.valid_until && (
                <p className="mt-1 text-sm text-error-600">{errors.valid_until}</p>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6 border-t border-neutral-200">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {existingDiscount ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Icon name="Check" size={20} className="mr-2" />
                {existingDiscount ? 'Update Discount' : 'Create Discount'}
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
